import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type PropsWithChildren,
    type ReactNode
} from 'react';
import { useMedia } from '../hooks/use-media';
import { setBodyClass } from '../util/Functions';
import { setMarkerSdkVisible } from '../util/marker-io-registry';
import '../components/layout/side-panel.css';

export type SidePanelId = 'admin' | 'ai-chat' | 'crm';

/**
 * One width for all three: they share the same strip of screen and only one is ever open, so a
 * narrower Hubby only made the layout jump by 80px as the user moved between tabs.
 *
 * Kept per-panel rather than collapsed to a constant — the map is where a panel that genuinely
 * needs its own width would say so, and `side-panel.css` mirrors it the same way.
 */
export const SIDE_PANEL_WIDTHS: Record<SidePanelId, number> = {
    admin: 640,
    'ai-chat': 640,
    crm: 640
};

export const SIDE_PANEL_TRANSITION_MS = 300;

/** Remembers the CRM panel across reloads, the same way `ai-chat-open` does for Hubby. */
export const CRM_OPEN_STORAGE_KEY = 'crm-open';

function readSidePanelWidth(): string {
    return document.body.style.getPropertyValue('--side-panel-width').trim();
}

/**
 * ROBO-591 — a message another part of the page has asked Hubby to send. `id` is unique per ask, so
 * asking the same words twice is two requests, and the panel clears exactly the one it sent.
 */
export interface HubbyPromptRequest {
    id: number;
    prompt: string;
}

interface SidePanelContextValue {
    openPanel: SidePanelId | null;
    togglePanel: (panel: SidePanelId) => void;
    closePanel: () => void;
    isPanelOpen: (panel: SidePanelId) => boolean;
    /**
     * ROBO-591 — open Hubby (never toggles it shut) and have the panel send `prompt` as the user's
     * next message, through the same `sendPrompt` path a suggestion chip uses. The one channel for
     * "Ask Hubby" from elsewhere in the app; the dashboard's Setup progress card is the first caller.
     *
     * Only meaningful where the panel is mounted (`canUseHubby`) — a caller must hide its button
     * otherwise, or the request waits here unsent until the next ask replaces it.
     */
    askHubby: (prompt: string) => void;
    /** The ask the panel has not sent yet, or null. Read by the panel only. */
    hubbyPromptRequest: HubbyPromptRequest | null;
    /** The panel's acknowledgement that it has taken request `id`. A stale id is ignored. */
    clearHubbyPromptRequest: (id: number) => void;
}

const SidePanelContext = createContext<SidePanelContextValue | null>(null);

function readInitialOpenPanel(): SidePanelId | null {
    if ((window.localStorage.getItem('admin-open') || '').length > 0) {
        return 'admin';
    }
    if (window.localStorage.getItem('ai-chat-open') === '1') {
        return 'ai-chat';
    }
    if (window.localStorage.getItem(CRM_OPEN_STORAGE_KEY) === '1') {
        return 'crm';
    }
    return null;
}

export function SidePanelProvider({ children }: PropsWithChildren<{ children?: ReactNode }>) {
    const { isMobile } = useMedia();
    const [openPanel, setOpenPanel] = useState<SidePanelId | null>(readInitialOpenPanel);
    const skipLayoutAnimationRef = useRef(readInitialOpenPanel() !== null);
    const layoutPanelRef = useRef<SidePanelId | null>(readInitialOpenPanel());

    /**
     * ★ ONE PANEL AT A TIME, BY CONSTRUCTION. `openPanel` is a single id rather than a set, so
     * opening admin, Hubby or CRM closes whichever of the other two was showing — they share the
     * same strip of screen and the same `--side-panel-width` the layout shrinks against, and two
     * at once has no coherent width.
     */
    const togglePanel = useCallback((panel: SidePanelId) => {
        setOpenPanel(current => (current === panel ? null : panel));
    }, []);

    const closePanel = useCallback(() => {
        setOpenPanel(null);
    }, []);

    const isPanelOpen = useCallback((panel: SidePanelId) => openPanel === panel, [openPanel]);

    const [hubbyPromptRequest, setHubbyPromptRequest] = useState<HubbyPromptRequest | null>(null);
    const nextHubbyPromptIdRef = useRef(0);

    const askHubby = useCallback((prompt: string) => {
        const text = prompt.trim();
        if (!text) {
            return;
        }
        nextHubbyPromptIdRef.current += 1;
        setHubbyPromptRequest({ id: nextHubbyPromptIdRef.current, prompt: text });
        // Open, never toggle: asking Hubby something must not close the panel it is already in.
        setOpenPanel('ai-chat');
    }, []);

    const clearHubbyPromptRequest = useCallback((id: number) => {
        setHubbyPromptRequest(current => (current?.id === id ? null : current));
    }, []);

    useEffect(() => {
        if (openPanel === 'ai-chat') {
            window.localStorage.setItem('ai-chat-open', '1');
        } else {
            window.localStorage.setItem('ai-chat-open', '');
        }
        window.localStorage.setItem(CRM_OPEN_STORAGE_KEY, openPanel === 'crm' ? '1' : '');
    }, [openPanel]);

    useEffect(() => {
        if (isMobile) {
            setBodyClass('root-side-panel-open', false);
            document.body.style.removeProperty('--side-panel-width');
            delete document.body.dataset.sidePanel;
            layoutPanelRef.current = null;
            return;
        }

        let cancelled = false;
        let closeTimer: ReturnType<typeof setTimeout> | undefined;

        const finishClose = () => {
            if (cancelled) {
                return;
            }
            setBodyClass('root-side-panel-open', false);
            document.body.style.removeProperty('--side-panel-width');
            delete document.body.dataset.sidePanel;
            layoutPanelRef.current = null;
        };

        const handleCloseTransitionEnd = (event: TransitionEvent) => {
            if (event.target !== document.body || event.propertyName !== '--side-panel-width') {
                return;
            }
            document.body.removeEventListener('transitionend', handleCloseTransitionEnd);
            if (closeTimer) {
                clearTimeout(closeTimer);
                closeTimer = undefined;
            }
            finishClose();
        };

        if (openPanel) {
            layoutPanelRef.current = openPanel;
            document.body.dataset.sidePanel = openPanel;
            setBodyClass('root-side-panel-open', true);
            const target = `${SIDE_PANEL_WIDTHS[openPanel]}px`;

            if (skipLayoutAnimationRef.current) {
                document.body.style.setProperty('--side-panel-width', target);
                skipLayoutAnimationRef.current = false;
            } else {
                const currentWidth = readSidePanelWidth();
                if (!currentWidth || currentWidth === '0px') {
                    document.body.style.setProperty('--side-panel-width', '0px');
                }
                requestAnimationFrame(() => {
                    if (!cancelled) {
                        document.body.style.setProperty('--side-panel-width', target);
                    }
                });
            }
        } else {
            const currentWidth = readSidePanelWidth();
            if (!currentWidth || currentWidth === '0px') {
                finishClose();
            } else {
                document.body.addEventListener('transitionend', handleCloseTransitionEnd);
                closeTimer = setTimeout(() => {
                    document.body.removeEventListener('transitionend', handleCloseTransitionEnd);
                    finishClose();
                }, SIDE_PANEL_TRANSITION_MS + 50);
                document.body.style.setProperty('--side-panel-width', '0px');
            }
        }

        return () => {
            cancelled = true;
            document.body.removeEventListener('transitionend', handleCloseTransitionEnd);
            if (closeTimer) {
                clearTimeout(closeTimer);
            }
        };
    }, [openPanel, isMobile]);

    useEffect(() => {
        setMarkerSdkVisible(openPanel === null && !isMobile);
    }, [openPanel]);

    const value = useMemo(
        () => ({
            openPanel,
            togglePanel,
            closePanel,
            isPanelOpen,
            askHubby,
            hubbyPromptRequest,
            clearHubbyPromptRequest
        }),
        [
            openPanel,
            togglePanel,
            closePanel,
            isPanelOpen,
            askHubby,
            hubbyPromptRequest,
            clearHubbyPromptRequest
        ]
    );

    return <SidePanelContext.Provider value={value}>{children}</SidePanelContext.Provider>;
}

export function useSidePanel() {
    const context = useContext(SidePanelContext);
    if (!context) {
        throw new Error('useSidePanel must be used within a SidePanelProvider');
    }
    return context;
}

/**
 * Which side panel is open, or null — including when there is no provider at all.
 *
 * ★ DELIBERATELY DOES NOT THROW, unlike {@link useSidePanel}. This exists for shared components
 * that render in every shell and must not care whether a `SidePanelProvider` is above them: the
 * `Drawer` wrapper is used in 59 places across hub, studio and delivery, and making it throw where
 * no provider happens to be mounted would trade one bug for a blank screen.
 *
 * "No provider" and "no panel open" are the same answer to the only question a consumer asks here —
 * is another surface sharing the screen right now — so they are deliberately not distinguished.
 */
export function useOpenSidePanel(): SidePanelId | null {
    return useContext(SidePanelContext)?.openPanel ?? null;
}
